/**
 * 请求去重工具
 * 防止短时间内重复的API调用，提升性能
 */

import React from 'react';

interface PendingRequest {
  promise: Promise<any>;
  timestamp: number;
}

class RequestDeduplication {
  private pendingRequests = new Map<string, PendingRequest>();
  private readonly CACHE_DURATION = 1000; // 1秒内的重复请求会被去重

  /**
   * 生成请求的唯一键
   */
  private generateKey(url: string, options?: any): string {
    const optionsStr = options ? JSON.stringify(options) : '';
    return `${url}:${optionsStr}`;
  }

  /**
   * 清理过期的请求
   */
  private cleanExpiredRequests(): void {
    const now = Date.now();
    const keysToDelete: string[] = [];
    
    this.pendingRequests.forEach((request, key) => {
      if (now - request.timestamp > this.CACHE_DURATION) {
        keysToDelete.push(key);
      }
    });
    
    keysToDelete.forEach(key => {
      this.pendingRequests.delete(key);
    });
  }

  /**
   * 去重的请求方法
   */
  async request<T>(
    url: string, 
    requestFn: () => Promise<T>,
    options?: any
  ): Promise<T> {
    const key = this.generateKey(url, options);
    
    // 清理过期请求
    this.cleanExpiredRequests();
    
    // 检查是否有相同的请求正在进行
    const existingRequest = this.pendingRequests.get(key);
    if (existingRequest) {
      console.log(`🔄 请求去重: ${url}`);
      return existingRequest.promise;
    }

    // 创建新请求
    const promise = requestFn().finally(() => {
      // 请求完成后清理
      this.pendingRequests.delete(key);
    });

    // 缓存请求
    this.pendingRequests.set(key, {
      promise,
      timestamp: Date.now()
    });

    return promise;
  }

  /**
   * 清空所有缓存的请求
   */
  clear(): void {
    this.pendingRequests.clear();
  }

  /**
   * 获取当前缓存的请求数量
   */
  getSize(): number {
    return this.pendingRequests.size;
  }
}

// 创建全局实例
export const requestDeduplication = new RequestDeduplication();

/**
 * 装饰器函数，用于API方法
 */
export function withDeduplication<T extends (...args: any[]) => Promise<any>>(
  originalMethod: T,
  keyGenerator?: (...args: any[]) => string
): T {
  return (async (...args: any[]) => {
    const key = keyGenerator ? keyGenerator(...args) : JSON.stringify(args);
    
    return requestDeduplication.request(
      key,
      () => originalMethod(...args)
    );
  }) as T;
}

/**
 * React Hook，用于组件中的请求去重
 */
export function useRequestDeduplication() {
  const request = React.useCallback(async <T>(
    key: string,
    requestFn: () => Promise<T>
  ): Promise<T> => {
    return requestDeduplication.request(key, requestFn);
  }, []);

  return { request };
}

export default requestDeduplication; 